import { useEffect, useRef } from 'react';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import type { PlanningEntityIdentifier, PlanningEntityType } from 'generated';

import { claimHighlight, releaseHighlight } from '../dragHighlight';
import {
  buildSlotDropData,
  DROP_SLOT_ATTRIBUTE,
  isPlanningCardSource
} from '../columnSlots';

interface UseDropTargetProps {
  /**
   * Identifies this slot within its column, so the cards next to it can find
   * the element to show their drop feedback on. The slot's event id.
   */
  slotKey: string;
  scheduleStart: string;
  start: string;
  planningEntityType: PlanningEntityType;
  planningEntityId: string;
  planningEntityIdentifier: PlanningEntityIdentifier;
  /**
   * Registers no drop target while false, so nothing can be dropped here.
   * Everything that makes a slot undroppable — the column is loading, the slot
   * is a non-droppable travel time — belongs in this one flag.
   */
  canDrop?: boolean;
}

/**
 * Registers a slot in a route column as a Pragmatic drop target.
 *
 * The slot owns the moment an appointment dropped here is planned at, and it
 * owns the feedback that shows it: the drop indicator its styles reveal on
 * `[data-drag-over]`. Cards delegate both to it — see `DraggableCardPragmatic`
 * — so the same feedback appears whether the pointer sits on the slot or on the
 * half of the neighbouring card that lands here.
 */
export const useDropTarget = ({
  slotKey,
  scheduleStart,
  start,
  planningEntityType,
  planningEntityId,
  planningEntityIdentifier,
  canDrop = true
}: UseDropTargetProps) => {
  const ref = useRef<HTMLLIElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !canDrop) return;

    // Present exactly while a target is registered, so a card's edge can only
    // ever delegate to a slot that accepts the drop.
    el.setAttribute(DROP_SLOT_ATTRIBUTE, slotKey);

    const cleanup = dropTargetForElements({
      element: el,
      canDrop: ({ source }) => isPlanningCardSource(source.data),
      getData: () =>
        buildSlotDropData(start, {
          scheduleStart,
          planningEntityType,
          planningEntityId,
          planningEntityIdentifier
        }),
      onDragEnter: () => claimHighlight(el, el),
      onDragLeave: () => releaseHighlight(el),
      onDrop: () => releaseHighlight(el)
    });
    return () => {
      cleanup();
      el.removeAttribute(DROP_SLOT_ATTRIBUTE);
      releaseHighlight(el);
    };
  }, [
    slotKey,
    scheduleStart,
    start,
    planningEntityId,
    planningEntityType,
    planningEntityIdentifier,
    canDrop
  ]);

  return ref;
};
